/**
 * GPS Background diagnostic widget — Profilo → Diagnostica.
 *
 * Verifies that background location tracking ACTUALLY works on this
 * device. The classic symptom we're diagnosing: a walk polyline goes
 * straight after the user locks the screen — caused by iOS suspending
 * the GPS when the app has only "While Using" permission (not "Always").
 *
 * Behavior:
 *   1. "Esegui" → probes @capgo/background-geolocation +
 *      @capacitor/geolocation availability and the current permission
 *      grant level. Shows a JSON report.
 *   2. "Richiedi permessi" → calls Geolocation.requestPermissions which
 *      surfaces the iOS permission sheet (this is what materializes the
 *      app's row in Settings → Privacy → Location the very first time).
 *   3. "Avvia test live" → starts a real BackgroundGeolocation stream
 *      with distanceFilter=0 (every fix logged) and appends every fix
 *      to a rolling 60-item buffer with a `bg: document.hidden` flag.
 *      Lock the screen / background the app and verify fixes keep
 *      arriving with `bg:true` — that's proof the stream survives.
 *   4. "Copia JSON" → puts the full report (info + fixes) on the
 *      clipboard for sharing with support.
 *
 * Critical cleanup:
 *   On unmount we call BackgroundGeolocation.stop(). Without this the
 *   diagnostic watcher stays alive and steals fixes from the actual
 *   Camminata page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useTheme } from '../context/ThemeContext';

const MAX_FIXES = 60;

/**
 * Get a handle to the native BackgroundGeolocation plugin via Capacitor's
 * registerPlugin (works WITHOUT @capgo/background-geolocation being in the
 * web bundle — the implementation lives in the iOS pod / Android module).
 * Returns null on web/PWA or when the plugin is not registered natively.
 */
const getBgGeoPlugin = () => {
  try {
    if (!Capacitor.isNativePlatform()) return null;
    if (!Capacitor.isPluginAvailable('BackgroundGeolocation')) return null;
    return registerPlugin('BackgroundGeolocation');
  } catch (_) {
    return null;
  }
};

export default function GpsBackgroundDiagnostic() {
  const { isDark } = useTheme();
  const [info, setInfo] = useState(null);
  const [permError, setPermError] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixes, setFixes] = useState([]);
  const startedAtRef = useRef(0);
  // Capture the start() promise so we can ignore the late rejection that
  // fires when we stop() — the plugin's start() promise only resolves
  // after stop().
  const trackerRef = useRef({ active: false });

  // ── Cleanup on unmount — CRITICAL: otherwise the diagnostic watcher
  //    keeps running and the Camminata page can't get fixes anymore.
  useEffect(() => {
    return () => {
      if (trackerRef.current.active) {
        try {
          const plugin = getBgGeoPlugin();
          if (plugin) plugin.stop();
        } catch (_) { /* ignore */ }
      }
    };
  }, []);

  const safe = useCallback(async (fn, fallback) => {
    try { return await fn(); }
    catch (e) { return { __error: String(e?.message || e), __fallback: fallback }; }
  }, []);

  // ── 1) Probe plugin + permission state ───────────────────────────────
  const checkPlugin = useCallback(async () => {
    setPermError(null);
    const isNative = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();
    const platform = (() => { try { return Capacitor.getPlatform(); } catch { return '?'; } })();

    const report = {
      isNative,
      platform,
      isPluginAvailable_BackgroundGeolocation: false,
      isPluginAvailable_Geolocation: false,
      pluginAvailable: false,
      pluginImportError: null,
      permissions: null,
      permissionsError: null,
    };

    if (!isNative) {
      setInfo(report);
      return;
    }

    try { report.isPluginAvailable_BackgroundGeolocation = Capacitor.isPluginAvailable('BackgroundGeolocation'); } catch (_) { /* ignore */ }
    try { report.isPluginAvailable_Geolocation = Capacitor.isPluginAvailable('Geolocation'); } catch (_) { /* ignore */ }

    // Use registerPlugin — no JS package needed for plugin availability
    try {
      const plugin = registerPlugin('BackgroundGeolocation');
      report.pluginAvailable = !!plugin && report.isPluginAvailable_BackgroundGeolocation;
    } catch (e) {
      report.pluginImportError = String(e?.message || e);
    }

    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const perms = await Geolocation.checkPermissions();
      report.permissions = perms;
    } catch (e) {
      report.permissionsError = String(e?.message || e);
    }

    setInfo(report);
  }, []);

  // ── 2) Request location permission (materializes Settings entry) ─────
  const requestPerms = useCallback(async () => {
    setPermError(null);
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      await Geolocation.requestPermissions({ permissions: ['location'] });
    } catch (e) {
      setPermError(String(e?.message || e));
    }
    // Refresh the report so the user sees the new grant level
    await checkPlugin();
  }, [checkPlugin]);

  // ── 3) Live tracker — every fix logged with `bg` flag ────────────────
  const startLiveTracker = useCallback(async () => {
    setFixes([]);
    startedAtRef.current = Date.now();
    const plugin = getBgGeoPlugin();
    if (!plugin) {
      // No native plugin → fall back to web geolocation (foreground only).
      // The web fallback won't keep working with screen locked — but at
      // least the user sees fixes when testing in the browser.
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        try {
          const id = navigator.geolocation.watchPosition(
            (pos) => {
              const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
              setFixes((prev) => [{
                time: new Date().toLocaleTimeString(),
                elapsed,
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: Math.round(pos.coords.accuracy || 0),
                speed: pos.coords.speed ?? null,
                bg: typeof document !== 'undefined' ? !!document.hidden : false,
                source: 'web',
              }, ...prev].slice(0, MAX_FIXES));
            },
            (err) => {
              setPermError(`Web GPS error: ${err.message}`);
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
          );
          trackerRef.current = { active: true, webId: id };
          setRunning(true);
        } catch (e) {
          setPermError(String(e?.message || e));
        }
      } else {
        setPermError('Background plugin non disponibile e nessun fallback web');
      }
      return;
    }
    // Native path — registerPlugin handle
    try {
      trackerRef.current = { active: true };
      // start() resolves only when stop() is called. Fire-and-forget +
      // swallow the late rejection — it's expected on stop.
      plugin.start(
        {
          backgroundTitle: 'Diagnostica GPS in corso',
          backgroundMessage: 'Test del tracking background. Blocca lo schermo per verificare i fix.',
          requestPermissions: true,
          stale: false,
          distanceFilter: 0,
        },
        (location, error) => {
          const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
          const time = new Date().toLocaleTimeString();
          // `document.hidden` is true when the screen is off or the app
          // is backgrounded — that's the smoking-gun signal we need.
          const bg = typeof document !== 'undefined' ? !!document.hidden : false;
          if (error) {
            setFixes((prev) => [{ time, elapsed, error: error.code || error.message || String(error), bg, source: 'native' }, ...prev].slice(0, MAX_FIXES));
            return;
          }
          if (!location) return;
          setFixes((prev) => [
            {
              time,
              elapsed,
              lat: location.latitude,
              lng: location.longitude,
              accuracy: Math.round(location.accuracy || 0),
              speed: location.speed ?? null,
              bg,
              source: 'native',
            },
            ...prev,
          ].slice(0, MAX_FIXES));
        }
      ).catch(() => { /* expected on stop / unavailable */ });
      setRunning(true);
    } catch (e) {
      setPermError(String(e?.message || e));
    }
  }, []);

  const stopLiveTracker = useCallback(async () => {
    const tr = trackerRef.current;
    try {
      if (tr.webId != null && typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(tr.webId);
      } else {
        const plugin = getBgGeoPlugin();
        if (plugin) {
          try { await plugin.stop(); } catch (_) { /* ignore */ }
        }
      }
    } catch (_) { /* ignore */ }
    trackerRef.current = { active: false };
    setRunning(false);
  }, []);

  // ── 4) Copy full report ──────────────────────────────────────────────
  const copyJson = useCallback(async () => {
    const payload = JSON.stringify({ info, fixes }, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
    } catch (_) {
      // eslint-disable-next-line no-alert
      window.alert(payload);
    }
  }, [info, fixes]);

  return (
    <div className="glass-card-sm p-3 mt-2" data-testid="diag-gps-section">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
          GPS Background
        </h3>
        <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {fixes.length} fix
        </span>
      </div>
      <p className={`text-[11px] mb-2 leading-snug ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
        Verifica che il tracking continui con schermo bloccato. iOS richiede permesso <b>&quot;Sempre&quot;</b>.
      </p>

      {/* Top row: probe + permission buttons */}
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        <button
          onClick={checkPlugin}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium active:scale-95 ${
            isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900 border border-gray-200'
          }`}
          data-testid="diag-gps-check-btn"
        >
          Esegui
        </button>
        <button
          onClick={requestPerms}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium active:scale-95 ${
            isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900 border border-gray-200'
          }`}
          data-testid="diag-gps-request-perm-btn"
        >
          Richiedi permessi
        </button>
      </div>

      {/* Bottom row: start + STOP always visible + copy log */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          onClick={startLiveTracker}
          disabled={running}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold active:scale-95 ${
            running ? 'bg-[#E8334A]/40 text-white/70 cursor-not-allowed' : 'bg-[#E8334A] text-white'
          }`}
          data-testid="diag-gps-start-btn"
        >
          ▶ Avvia
        </button>
        <button
          onClick={stopLiveTracker}
          disabled={!running}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold active:scale-95 ${
            !running
              ? isDark ? 'bg-white/5 text-white/40 cursor-not-allowed' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
              : isDark ? 'bg-white/20 text-white' : 'bg-gray-700 text-white'
          }`}
          data-testid="diag-gps-stop-btn"
        >
          ■ Stop
        </button>
        <button
          onClick={copyJson}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold active:scale-95 ${
            isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900 border border-gray-200'
          }`}
          data-testid="diag-gps-copy-btn"
        >
          📋 Copia log
        </button>
      </div>

      {running && (
        <div className={`text-xs rounded-lg p-2 mb-2 ${
          isDark
            ? 'text-amber-300 bg-amber-500/10 border border-amber-500/30'
            : 'text-amber-900 bg-amber-50 border border-amber-300'
        }`}>
          ▶ Test attivo. Blocca lo schermo o metti l&apos;app in background per verificare che i fix continuino ad arrivare.
        </div>
      )}

      {permError && (
        <div className={`text-xs rounded-lg p-2 mb-2 ${
          isDark
            ? 'text-red-300 bg-red-500/10 border border-red-500/30'
            : 'text-red-900 bg-red-50 border border-red-300'
        }`}>
          Errore: {permError}
        </div>
      )}

      {info && (
        <div className="mb-2">
          <div className={`text-[10px] uppercase tracking-wide mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            Info plugin &amp; permessi
          </div>
          <pre
            className={`text-[10px] rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all ${
              isDark ? 'text-white/80 bg-black/30' : 'text-gray-800 bg-gray-100 border border-gray-200'
            }`}
            data-testid="diag-gps-info-output"
          >
            {JSON.stringify(info, null, 2)}
          </pre>
        </div>
      )}

      <div>
        <div className={`text-[10px] uppercase tracking-wide mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
          Fix GPS (più recenti in cima)
        </div>
        <pre
          className={`text-[10px] rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto ${
            isDark ? 'text-white/80 bg-black/30' : 'text-gray-800 bg-gray-100 border border-gray-200'
          }`}
          data-testid="diag-gps-fixes-output"
        >
          {fixes.length === 0 ? '(nessun fix ricevuto ancora)' : fixes.map((f) => JSON.stringify(f)).join('\n')}
        </pre>
      </div>
    </div>
  );
}
